import React, { useRef, useEffect } from 'react';
import { classNames } from '../../utilities';
import '../../base.scss';
import './Autocomplete.scss';

export interface AutocompleteSuggestionProps {
  /** Child text or nodes to render */
  children?: string | string[] | React.ReactNode;
  /** CSS class name to apply */
  className?: string;
  /** An index representing where this suggestion appears in a list */
  index: number;
  /** Whether this suggestions should have browser focus */
  isFocused?: boolean;
  /** Whether this suggestion should have highlight style applied */
  isHighlighted?: boolean;
  /** Callback on click of this suggestion */
  onClick?: (index: number, value: string) => void;
  /** Callback when this suggestion is focused */
  onFocus?: (index: number, value: string) => void;
  /** The value of this suggestion, returned in the callback (can be distinct from text displayed) */
  value: string;
}

/**
 * Represents a single autocomplete suggestion item.
 * 
 * Listens for clicks and focus on itself. Maintains a ref
 * on its outer div so we can set focus via a prop.
 */
export function AutocompleteSuggestion(props: AutocompleteSuggestionProps) {
  const {
    children,
    className = '',
    index,
    isFocused = false,
    isHighlighted = false,
    onClick,
    onFocus,
    value
  } = props;
  
  const itemRef = useRef<HTMLDivElement>(null);
  
  // useEffect here prevents 
  // "Cannot update a component while rendering a different component"
  useEffect(() => {
    if (isFocused) {
      if (itemRef && itemRef.current) {
        itemRef.current.focus();
      }
    }
  }, [isFocused]);

  const suggestionClasses = classNames(
    'ps-autocomplete__suggestion',
    isHighlighted ? 'ps-autocomplete__suggestion--highlighted' : '',
    className
  );

  return (
    <div 
      className={suggestionClasses}
      tabIndex={0}
      onClick={() => onClick && onClick(index, value)}
      onFocus={() => onFocus && onFocus(index, value)}
      ref={itemRef}
    >
      {children}
    </div>
  );
}